import React, { useState, useEffect, useCallback } from 'react';
import { activityLogService } from '../services/api';
import { EstadoDaLista, useEstadoDaCarga } from '../design';
import { fmtDateTime } from '../utils/formatters';
import { TONE_BADGE } from '../utils/statusColors';

interface ActivityLog {
  id: number;
  action: string;
  details: string;
  userId: number | null;
  userName: string;
  entityType: string;
  entityId: number;
  createdAt: string;
}

/**
 * O selo da ação (09/10/2026). Eram cinco matizes (verde, azul, vermelho, roxo, laranja) para dizer o que o texto ao
 * lado já diz: cor como estado. Agora o selo é neutro, e o que pesa é a palavra.
 */
const ROTULO_DA_ACAO: Record<string, string> = {
  Criou: 'Criado',
  Atualizou: 'Atualizado',
  Excluiu: 'Excluído',
  Visualizou: 'Visualizado',
};

interface Props {
  leadId: number;
}

/**
 * O Histórico do lead. ⛔ A carga que falhava virava lista vazia, e a tela dizia "Nenhuma atividade registrada"; e o
 * "Atualizar" que falhava APAGAVA a lista que estava na tela (09/10/2026). Agora a carga é estado (useEstadoDaCarga):
 * a lista só é trocada quando a leitura dá certo, e o EstadoDaLista diz o que aconteceu.
 */
export default function LeadHistoryTab({ leadId }: Props) {
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const carga = useEstadoDaCarga();
  const { rodar } = carga;

  const loadLogs = useCallback(() => rodar(async () => {
    const data = await activityLogService.getByEntity('lead', leadId);
    setLogs(Array.isArray(data) ? data : []);
  }), [leadId, rodar]);

  useEffect(() => { loadLogs(); }, [loadLogs]);

  return (
    <div className="p-5 overflow-y-auto flex-1">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-slate-300">
          Historico de Atividades{carga.atualizadoEm ? ` (${logs.length})` : ''}
        </h3>
        <button
          onClick={loadLogs}
          className="text-xs text-slate-400 hover:text-white transition-colors px-2 py-1 rounded hover:bg-slate-700"
        >
          Atualizar
        </button>
      </div>

      <EstadoDaLista
        carga={carga}
        temDados={logs.length > 0}
        oQue="o histórico"
        onTentarDeNovo={loadLogs}
        vazio={
          <div className="flex flex-col items-center justify-center py-16 text-slate-500">
            <p className="text-sm">Nenhuma atividade registrada</p>
            <p className="text-xs mt-1 text-slate-600">As atividades aparecerao aqui conforme o lead for atualizado</p>
          </div>
        }
      >
        <div className="relative">
          {/* Vertical line */}
          <div className="absolute left-[11px] top-2 bottom-2 w-px bg-slate-700" />

          <div className="space-y-0">
            {logs.map((log) => (
              <div key={log.id} className="relative flex gap-3 py-3 group">
                {/* Dot */}
                <div className="relative z-10 flex-shrink-0 w-[23px] h-[23px] rounded-full border-2 border-slate-600 bg-slate-800 flex items-center justify-center mt-0.5">
                  <div className="w-2 h-2 rounded-full bg-slate-400" />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded ${TONE_BADGE.neutral}`}>
                      {ROTULO_DA_ACAO[log.action] || 'Evento'}
                    </span>
                    <span className="text-xs text-slate-500">{fmtDateTime(log.createdAt)}</span>
                  </div>
                  <p className="text-sm text-slate-300 mt-1 leading-relaxed">{log.details}</p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    por <span className="text-slate-400">{log.userName}</span>
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </EstadoDaLista>
    </div>
  );
}
